import React from 'react';
import { BookEventGuestCounter } from '../../book-event/guest-counter/BookEventGuestCounter';
import { EventDetailsSummary } from '../../event-details-summary/EventDetailsSummary';
import { SpotAvailabilityLine } from '../../event-details-summary/SpotAvailabilityLine';
import { ExpandableDescription } from '../../expandable-description/ExpandableDescription';
import type { BookingPurchaseDialogData } from '../BookingPurchaseDialog.types';
import { DialogFooter } from '../footer/DialogFooter';
import { MembershipSummary } from '../membership-summary/MembershipSummary';
import {
  PaymentOptionRow,
  PaymentSection,
  StartDateSection,
} from '../payment-section/PaymentSection';
import { PriceBreakdown } from '../price-breakdown/PriceBreakdown';
import { PromoCodeField } from '../promo-code-field/PromoCodeField';
import { TermsCheckboxGroup } from '../terms/TermsCheckboxGroup';
import { DialogWarnings } from '../warnings/DialogWarnings';

type Props = {
  data: BookingPurchaseDialogData;
};

type ViewProps = Props & {
  isPurchase: boolean;
};

function BookingActionsGroup({
  guestCounter,
  payment,
}: {
  guestCounter?: BookingPurchaseDialogData['guestCounter'];
  payment?: BookingPurchaseDialogData['payment'];
}) {
  if (!guestCounter && !payment) {
    return null;
  }

  return (
    <>
      {guestCounter ? (
        <div className="content-group">
          <ul className="list">
            <li>
              <BookEventGuestCounter data={guestCounter} />
            </li>
          </ul>
        </div>
      ) : null}
      {payment ? (
        <div className="content-group">
          <PaymentOptionRow data={payment} />
        </div>
      ) : null}
    </>
  );
}

function DialogPrice({
  priceLabel,
  priceLabelId,
}: {
  priceLabel?: string | null;
  priceLabelId?: string | null;
}) {
  if (!priceLabel) {
    return null;
  }

  return (
    <div className="price-wrapper">
      <span id={priceLabelId || undefined}>{priceLabel}</span>
    </div>
  );
}

function DialogHeading({ data, isPurchase }: ViewProps) {
  if (!data.title && !data.priceLabel) {
    return null;
  }

  return (
    <div className={isPurchase ? 'content-group' : 'col s12'}>
      <div className="title-wrapper">
        {isPurchase ? <h3>{data.title}</h3> : <h4>{data.title}</h4>}
      </div>
      <DialogPrice priceLabel={data.priceLabel} priceLabelId={data.priceLabelId} />
    </div>
  );
}

function DialogDescription({ data }: Props) {
  if (!data.description) {
    return null;
  }

  return (
    <div className="content-group event-description">
      <ExpandableDescription data={data.description} />
    </div>
  );
}

function EventSummary({ data }: Props) {
  if (!data.eventDetailsSummary) {
    return null;
  }

  return (
    <div className="content-group-extra event-details-summary">
      <EventDetailsSummary data={data.eventDetailsSummary} />
    </div>
  );
}

function DialogSummary({ data, isPurchase }: ViewProps) {
  if (!isPurchase) {
    return <EventSummary data={data} />;
  }

  return data.membershipSummary ? (
    <MembershipSummary data={data.membershipSummary} />
  ) : null;
}

function DialogPromoCode({ data }: Props) {
  const promo = data.priceBreakdown?.promo;

  return promo ? <PromoCodeField promo={promo} /> : null;
}

function DialogStartDate({ data, isPurchase }: ViewProps) {
  if (!isPurchase || !data.payment) {
    return null;
  }

  return <StartDateSection data={data.payment} />;
}

function DialogPriceBreakdown({ data }: Props) {
  if (!data.priceBreakdown) {
    return null;
  }

  return <PriceBreakdown data={data.priceBreakdown} />;
}

function DialogPaymentArea({ data, isPurchase }: ViewProps) {
  if (!isPurchase) {
    return (
      <BookingActionsGroup guestCounter={data.guestCounter} payment={data.payment} />
    );
  }

  return data.payment ? <PaymentSection data={data.payment} /> : null;
}

function DialogSpotAvailability({ data }: Props) {
  if (!data.spotAvailability) {
    return null;
  }

  return (
    <div className="spot-availability">
      <SpotAvailabilityLine data={data.spotAvailability} />
    </div>
  );
}

function DialogTerms({ data }: Props) {
  if (!data.terms) {
    return null;
  }

  return (
    <div className="content-group-extra content-group-tos">
      <TermsCheckboxGroup data={data.terms} />
    </div>
  );
}

export function MainView({ data }: Props) {
  const isPurchase = data.variant === 'buy-membership';
  const bodyClass = isPurchase
    ? 'modal-body modal-bookbuy modal-purchase'
    : 'modal-body modal-bookbuy';

  return (
    <div className={bodyClass}>
      <div className="row">
        <div className={isPurchase ? 'col s12' : 'content-group'}>
          <DialogWarnings warnings={data.warnings || []} />
          <DialogHeading data={data} isPurchase={isPurchase} />
          <DialogDescription data={data} />
          <DialogSummary data={data} isPurchase={isPurchase} />
          <DialogPromoCode data={data} />
          <DialogStartDate data={data} isPurchase={isPurchase} />
          <DialogPriceBreakdown data={data} />
          <DialogPaymentArea data={data} isPurchase={isPurchase} />
          <DialogSpotAvailability data={data} />
          <DialogTerms data={data} />
          <DialogFooter
            footerButtons={data.footerButtons}
            documentLinks={data.documentLinks}
          />
        </div>
      </div>
    </div>
  );
}
